import React from 'react';

interface Props {
  content: string;
}

const inlinePattern = /(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|`([^`]+)`|\*\*([^*]+)\*\*|__([^_]+)__|\*([^*]+)\*|_([^_]+)_)/g;

function renderInline(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(inlinePattern)) {
    const index = match.index ?? 0;
    if (index > lastIndex) nodes.push(text.slice(lastIndex, index));

    if (match[2] && match[3]) {
      nodes.push(
        <a
          key={index}
          href={match[3]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#5624d0] underline underline-offset-2"
        >
          {match[2]}
        </a>
      );
    } else if (match[4]) {
      nodes.push(<code key={index} className="rounded bg-black/10 px-1 py-0.5 font-mono text-[0.9em]">{match[4]}</code>);
    } else if (match[5] || match[6]) {
      nodes.push(<strong key={index}>{match[5] || match[6]}</strong>);
    } else {
      nodes.push(<em key={index}>{match[7] || match[8]}</em>);
    }
    lastIndex = index + match[0].length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

const MarkdownContent: React.FC<Props> = ({ content }) => {
  const lines = content.replace(/\r\n?/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (!line.trim()) {
      index += 1;
      continue;
    }

    if (/^\s{0,3}([-*_])(?:\s*\1){2,}\s*$/.test(line)) {
      blocks.push(<hr key={`rule-${index}`} className="my-2 border-gray-300" />);
      index += 1;
      continue;
    }

    if (line.startsWith('```')) {
      const language = line.slice(3).trim();
      const code: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].startsWith('```')) code.push(lines[index++]);
      if (index < lines.length) index += 1;
      blocks.push(
        <pre key={`code-${index}`} className="my-2 overflow-x-auto rounded-lg bg-gray-900 p-3 text-xs text-gray-100">
          <code className={language ? `language-${language}` : undefined}>{code.join('\n')}</code>
        </pre>
      );
      continue;
    }

    const heading = /^(#{1,6})\s+(.+)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      blocks.push(
        <div key={`heading-${index}`} role="heading" aria-level={level} className="mb-1 mt-2 font-bold">
          {renderInline(heading[2])}
        </div>
      );
      index += 1;
      continue;
    }

    const unordered = /^\s*[-*+]\s+(.+)$/.exec(line);
    const ordered = /^\s*\d+[.)]\s+(.+)$/.exec(line);
    if (unordered || ordered) {
      const items: React.ReactNode[] = [];
      const isOrdered = Boolean(ordered);
      const itemPattern = isOrdered ? /^\s*\d+[.)]\s+(.+)$/ : /^\s*[-*+]\s+(.+)$/;
      while (index < lines.length) {
        const item = itemPattern.exec(lines[index]);
        if (!item) break;
        items.push(<li key={index}>{renderInline(item[1])}</li>);
        index += 1;
      }
      const className = `my-1 pl-5 ${isOrdered ? 'list-decimal' : 'list-disc'}`;
      blocks.push(isOrdered ? <ol key={`list-${index}`} className={className}>{items}</ol> : <ul key={`list-${index}`} className={className}>{items}</ul>);
      continue;
    }

    if (/^>\s?/.test(line)) {
      const quote: string[] = [];
      while (index < lines.length && /^>\s?/.test(lines[index])) quote.push(lines[index++].replace(/^>\s?/, ''));
      blocks.push(<blockquote key={`quote-${index}`} className="my-2 border-l-4 border-gray-300 pl-3 italic">{quote.map((part, i) => <React.Fragment key={i}>{renderInline(part)}{i < quote.length - 1 && <br />}</React.Fragment>)}</blockquote>);
      continue;
    }

    const paragraph: string[] = [line];
    index += 1;
    while (index < lines.length && lines[index].trim() && !/^(#{1,6})\s|^```|^\s*([-*_])(?:\s*\1){2,}\s*$|^\s*[-*+]\s+|^\s*\d+[.)]\s+|^>\s?/.test(lines[index])) {
      paragraph.push(lines[index++]);
    }
    blocks.push(<p key={`paragraph-${index}`} className="my-1">{paragraph.map((part, i) => <React.Fragment key={i}>{renderInline(part)}{i < paragraph.length - 1 && <br />}</React.Fragment>)}</p>);
  }

  return <div className="markdown-content first:mt-0 last:mb-0">{blocks}</div>;
};

export default MarkdownContent;
